import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { useLocation, useNavigate } from "react-router";

import { ApiError } from "../../api/client.ts";
import { useLogin } from "../../api/hooks.ts";
import { Icon } from "../../ui/Icon.tsx";
import { useTheme } from "../../ui/theme.ts";

/**
 * La puerta, con el diseno que entrego el propietario (login_nuevo.html),
 * portado entero: escena vectorial a la izquierda, panel con fondo animado a la
 * derecha, tarjeta de vidrio, tres instrumentos y la cita rotatoria del pie.
 *
 * Del contrato solo hay una forma de entrar: `POST /api/v1/auth/login` con
 * `{ username, password }` y una cookie firmada de vuelta (ADR 0005, punto 1).
 * Nada de tokens en localStorage, donde cualquier script de la pagina podria
 * leerlos, y nada de recordar el usuario en el navegador tampoco: lo que se
 * escribe aqui no sobrevive al envio.
 *
 * Lo que el diseno traia y NO se ha copiado literal, con su motivo, porque un
 * control muerto en la puerta de un producto que existe para medir con
 * honestidad es su contrario:
 *
 * 1. El campo era "Correo" con un valor de demo dentro. Aqui es "Usuario", que
 *    es lo que pide el contrato, y sin valor previo: una credencial de ejemplo
 *    en un formulario real acaba enviandose.
 * 2. Google y Apple siguen ahi, desactivados y diciendo por que. Ver el
 *    comentario junto a los dos botones.
 * 3. "Olvidaste tu contrasena" no llevaba a ningun sitio. No hay recuperacion,
 *    pero desde el ADR 0005 si hay administradores, y un administrador puede
 *    poner otra contrasena desde Usuarios. Eso es lo que dice ahora.
 * 4. "Recordarme" esta marcado y desactivado: la cookie ya vive 30 dias
 *    deslizantes SIEMPRE, asi que la casilla no elige nada. Para que elija de
 *    verdad hace falta que el contrato lo admita; mientras no lo admita, una
 *    casilla que se puede desmarcar y no cambia nada miente.
 * 5. "Crea tu bitacora" no existe: no hay autoregistro, las cuentas las crea un
 *    administrador (ADR 0005).
 * 6. Las cifras de `.art-stats` y de los tres instrumentos eran inventadas.
 *    Los dibujos se conservan intactos; las etiquetas, no. Ver el comentario de
 *    `PRODUCT_FACTS`.
 *
 * NO hay pantalla de "cambia la contrasena la primera vez", y no es un olvido:
 * el contrato no tiene ese paso. El administrador inicial nace SIN contrasena
 * utilizable --ninguna cadena autentica esa fila, ni la vacia-- y quien instala
 * le pone la suya desde la consola del servidor con `set-password`. Una
 * contrasena inicial conocida vale desde que arranca el servicio hasta que
 * alguien entra a cambiarla, y en ese hueco cualquiera que haya leido el
 * repositorio es administrador; la pantalla que "obliga a cambiarla" no cierra
 * ese hueco, solo lo decora.
 */
export function Login(): ReactNode {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle } = useTheme();

  /* La costura necesita saber DONDE acaba la curva de la escena, y eso solo se
     sabe midiendo: el nodo final esta dentro de un SVG que se recorta con
     `slice`, asi que su posicion depende del alto de la ventana. */
  const seam = useRef<HTMLDivElement>(null);
  const curveTip = useRef<SVGCircleElement>(null);
  const firstField = useRef<HTMLInputElement>(null);

  /* El foco se pone a mano y con `preventScroll`, no con `autoFocus`: en una
     ventana baja el navegador lleva el campo a la vista y de paso se come la
     cabecera con la marca y el conmutador de tema. */
  useEffect(() => {
    firstField.current?.focus({ preventScroll: true });
  }, []);

  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const error = login.error instanceof ApiError ? login.error : null;
  const userError = error?.fieldError("username");
  const passwordError = error?.fieldError("password");
  const canSubmit = username.trim() !== "" && password !== "";

  usePlacedSeam(seam, curveTip);

  return (
    <main className="login">
      {/* =================== MITAD IZQUIERDA =================== */}
      <section className="art">
        <Scene tipRef={curveTip} />

        <div className="art-copy">
          <span className="art-mark">
            <i />
            Tu diario de trading
          </span>
          <h1>
            Lo que registras,
            <br />
            lo mejoras.
          </h1>
          {/* El diseno cerraba esta frase con "que te esta costando dinero".
              Aqui no hay ni un campo de moneda en el dominio (SPEC 2): lo que
              se mide es el R y el plan, y prometer dinero seria prometer algo
              que el producto no calcula. */}
          <p>
            Anota el plan antes de entrar, cierra con los precios que de verdad
            ejecutaste y deja que el journal te ensene en R que te funciona y que
            te esta costando disciplina.
          </p>

          <div className="art-stats">
            {PRODUCT_FACTS.map((fact) => (
              <div key={fact.label}>
                <b>{fact.figure}</b>
                <span>{fact.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== MITAD DERECHA =================== */}
      <section className="lg-panel">
        <div className="panel-art">
          <PanelArt />
        </div>

        <header className="p-top">
          <span className="brand">
            <span className="brand-mark">
              <Icon name="trend" />
            </span>
            TradingJournal
          </span>
          <button
            type="button"
            className="theme-btn"
            onClick={toggle}
            title="Cambiar tema"
            aria-label={theme === "dark" ? "Pasar al tema claro" : "Pasar al tema oscuro"}
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </button>
        </header>

        <div className="p-body">
          <div className="lg-stack">
            <div className="card">
              <h2>Bienvenido de nuevo</h2>
              <p className="sub">Entra para registrar la sesion de hoy.</p>

              <form
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  login.mutate(
                    { username: username.trim(), password },
                    { onSuccess: () => navigate(from, { replace: true }) },
                  );
                }}
              >
                <label className="field">
                  <span>Usuario</span>
                  <div className="input">
                    <Icon name="users" />
                    <input
                      type="text"
                      autoComplete="username"
                      autoCapitalize="none"
                      spellCheck={false}
                      ref={firstField}
                      placeholder="tu usuario"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                    />
                  </div>
                  {userError !== undefined && <span className="err">{userError}</span>}
                </label>

                <label className="field">
                  <span>Contrasena</span>
                  <div className="input">
                    <Icon name="lock" />
                    <input
                      type={visible ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="Tu contrasena"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                    <button
                      type="button"
                      className="eye"
                      aria-label={visible ? "Ocultar la contrasena" : "Mostrar la contrasena"}
                      onClick={() => setVisible((current) => !current)}
                    >
                      <Icon name="eye" />
                    </button>
                  </div>
                  {passwordError !== undefined && <span className="err">{passwordError}</span>}
                </label>

                <div className="lg-row">
                  {/* Marcada y desactivada, no interactiva y a medias.
                      `LoginCommand` solo tiene `username` y `password`, y el
                      endpoint emite SIEMPRE `IsPersistent = true` con 30 dias
                      deslizantes. Es decir: hoy la sesion se recuerda pase lo
                      que pase, y una casilla que se puede desmarcar sin que
                      cambie nada es una promesa falsa. Para que elija de verdad
                      hacen falta dos lineas en el servidor --el campo
                      `RememberMe` en el comando y el `IsPersistent` colgando de
                      el en el endpoint--; en cuanto esten, esto pasa a
                      `checked={remember}` y el cuerpo lleva `rememberMe`. */}
                  <label
                    className="check"
                    title="La sesion ya se recuerda 30 dias en este dispositivo; todavia no se puede elegir lo contrario."
                  >
                    <input type="checkbox" checked disabled readOnly /> Recordarme
                  </label>
                  <span className="hint">
                    Si la olvidaste, pidesela a tu administrador: puede ponerte otra desde Usuarios.
                  </span>
                </div>

                {/* El resumen solo sale cuando el error NO es de un campo: si el
                    servidor dijo que falta el usuario, el mensaje ya esta junto
                    al usuario, y repetirlo arriba hace dudar de si son dos
                    fallos.

                    El texto del 401 y del 403 lo escribe el servidor a
                    proposito: dice "usuario o contrasena incorrectos" sin
                    revelar si el nombre existe, y en cambio si dice "esta cuenta
                    esta desactivada" a quien acerto la contrasena, que ya sabe
                    que la cuenta existe. */}
                {error !== null && userError === undefined && passwordError === undefined && (
                  <p className="errbox">
                    {error.status === 429
                      ? "Demasiados intentos. Espera unos minutos antes de volver a probar."
                      : error.message}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn primary"
                  disabled={login.isPending || !canSubmit}
                >
                  {login.isPending && <span className="spinner" />}
                  {login.isPending ? "Entrando" : "Entrar"}
                </button>
              </form>

              <div className="lg-sep">
                <span>o continua con</span>
              </div>

              {/* Google y Apple se quedan, desactivados. No hay OAuth en el
                  producto y no lo va a haber en la fase siguiente: para
                  activarlos hace falta registrar la aplicacion en cada
                  proveedor, guardar su identificador y su secreto fuera de
                  `appsettings`, anadir el intercambio de codigo por token en el
                  servidor y decidir como se une una identidad externa con la
                  fila de `users` (ADR 0005). Nada de eso esta.
                  Un boton desactivado que dice por que lo esta conserva el
                  diseno y no promete nada; uno que no hace nada al pulsarlo
                  hace dudar de si la aplicacion esta rota. */}
              <div className="social">
                <button
                  className="btn ghost"
                  type="button"
                  disabled
                  title="Entrar con Google todavia no esta disponible."
                >
                  <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="#EA4335"
                      d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1a6.2 6.2 0 0 1 0-12.4c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 3.1 14.7 2 12 2a10 10 0 1 0 0 20c5.8 0 9.6-4 9.6-9.7 0-.7-.1-1.2-.2-1.8H12z"
                    />
                  </svg>
                  Google
                </button>
                <button
                  className="btn ghost"
                  type="button"
                  disabled
                  title="Entrar con Apple todavia no esta disponible."
                >
                  <svg className="ic" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M16.4 12.8c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.9-1.5-.1-2.8.9-3.6.9-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.2 1.7 2.5 2.9 2.4 1.2 0 1.6-.7 3-.7s1.8.7 3 .7 2-1.1 2.8-2.3c.9-1.3 1.2-2.6 1.2-2.7 0 0-2.1-.8-2.1-3.2zM14.1 5.6c.6-.8 1.1-1.9 1-3-1 0-2.2.6-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.5z" />
                  </svg>
                  Apple
                </button>
              </div>

              {/* Una linea y no dos, y no es capricho: el diseno reserva aqui
                  el alto de UNA, y la segunda se comia justo el margen que la
                  columna necesita en una ventana baja. Dicho corto dice lo
                  mismo: no hay autoregistro. */}
              <p className="card-foot">Las cuentas las crea un administrador.</p>
            </div>

            {/* Los tres instrumentos. `aria-hidden` como en el diseno: repiten
                en corto lo que la escena de la izquierda ya dice con todas las
                palabras, y leerlo dos veces no ayuda a nadie. */}
            <div className="lg-gauges" aria-hidden="true">
              <div className="lg-gauge">
                <div className="g-top">
                  <b>{PRODUCT_FACTS[0].figure}</b>
                </div>
                <svg viewBox="0 0 100 34">
                  <path
                    className="g-track"
                    d="M6 30 A26 26 0 0 1 94 30"
                    fill="none"
                    strokeWidth="6"
                    strokeLinecap="round"
                  />
                  <path
                    className="g-arc"
                    style={vars({ "--len": "51" })}
                    d="M6 30 A26 26 0 0 1 94 30"
                    fill="none"
                    strokeWidth="6"
                    strokeLinecap="round"
                    pathLength="82"
                  />
                </svg>
                <small>campos de dinero</small>
              </div>

              <div className="lg-gauge">
                <div className="g-top">
                  <b>{PRODUCT_FACTS[1].figure}</b>
                </div>
                <svg viewBox="0 0 100 34">
                  <path
                    className="g-spark"
                    d="M4 28 L18 22 L32 25 L46 14 L60 18 L74 9 L96 5"
                    fill="none"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle className="node n4 g-tip" cx="96" cy="5" r="3" />
                </svg>
                <small>del calculo, en el servidor</small>
              </div>

              <div className="lg-gauge">
                <div className="g-top">
                  <b>{PRODUCT_FACTS[2].figure}</b>
                </div>
                <svg viewBox="0 0 100 34">
                  {GAUGE_BARS.map((bar, index) => (
                    <rect
                      key={bar.x}
                      className="g-bar"
                      style={vars({ "--i": String(index) })}
                      x={bar.x}
                      y={bar.y}
                      width="10"
                      height={bar.height}
                      rx="3"
                      opacity={bar.opacity}
                    />
                  ))}
                </svg>
                <small>tokens guardados aqui</small>
              </div>
            </div>
          </div>
        </div>

        <footer className="p-foot">
          <div className="quote">
            <span className="qi">
              <Icon name="quote" />
            </span>
            <div className="rot">
              <q>Un buen trade no es el que gana: es el que respeta el plan.</q>
              <q>La paciencia tambien es una posicion.</q>
              <q>Registra hoy lo que quieres mejorar manana.</q>
            </div>
          </div>
        </footer>
      </section>

      {/* La linea de precio continua desde la escena hacia el panel. */}
      <div className="seam" ref={seam} aria-hidden="true">
        <Seam />
      </div>
    </main>
  );
}

/**
 * Las tres cifras de la escena y de los instrumentos.
 *
 * El diseno ponia aqui "62% de acierto", "2.3 profit factor" y "14 dias
 * seguidos". Antes de iniciar sesion NO HAY SESION de la que sacar una cifra,
 * asi que cualquier numero de esos es inventado por definicion, y en la puerta
 * de un journal que existe para que la nota del examen no se pueda tocar un
 * numero falso es exactamente su contrario.
 *
 * Los DIBUJOS se conservan tal cual --el arco al 51 de 82, la chispa con sus
 * siete vertices y las barras con sus alturas y opacidades--: son decoracion y
 * no dicen nada. Lo que cambia son las etiquetas, y las tres que quedan se
 * pueden comprobar en el codigo: no hay ni un campo de moneda en el dominio
 * (SPEC 2), la aritmetica la hace entera el servidor (SPEC 15.1) y la sesion es
 * una cookie firmada, sin un solo token en el navegador (SPEC 19.18).
 */
const PRODUCT_FACTS = [
  { figure: "0", label: "campos de dinero: la unidad es el R" },
  { figure: "100%", label: "del calculo, hecho en el servidor" },
  { figure: "0", label: "tokens en el navegador: solo una cookie firmada" },
] as const;

/** Las siete barras del tercer instrumento, con las medidas del diseno. */
const GAUGE_BARS = [
  { x: 2, y: 18, height: 16, opacity: 0.35 },
  { x: 16, y: 12, height: 22, opacity: 0.45 },
  { x: 30, y: 21, height: 13, opacity: 0.35 },
  { x: 44, y: 8, height: 26, opacity: 0.6 },
  { x: 58, y: 15, height: 19, opacity: 0.5 },
  { x: 72, y: 4, height: 30, opacity: 0.8 },
  { x: 86, y: 10, height: 24, opacity: 1 },
] as const;

/**
 * `style` con propiedades personalizadas. `CSSProperties` no declara las `--*`,
 * y los retardos y longitudes del diseno viajan justo por ahi. La interseccion
 * las admite sin ningun `as` y sin `any`.
 */
type CssVars = CSSProperties & Record<`--${string}`, string>;

function vars(values: Record<`--${string}`, string>): CssVars {
  return values;
}

/**
 * La costura, colocada donde acaba la curva de la escena.
 *
 * Se mide con `getBoundingClientRect` porque no hay forma de saberlo de otra
 * manera: el SVG de la escena se recorta con `preserveAspectRatio="slice"` y el
 * nodo final cae en un sitio distinto segun el alto de la ventana. Por debajo
 * de 901 px no se dibuja --el diseno la oculta cuando las dos mitades se
 * apilan-- y el redimensionado se agrupa a 120 ms, que es lo que hacia el
 * fichero original: medir en cada pixel de arrastre son cientos de
 * `getBoundingClientRect` por segundo, y cada uno fuerza un calculo de estilo.
 */
function usePlacedSeam(
  seam: RefObject<HTMLDivElement | null>,
  tip: RefObject<SVGCircleElement | null>,
): void {
  useEffect(() => {
    let timer = 0;

    function place(): void {
      const host = seam.current;
      const anchor = tip.current;

      if (host === null || anchor === null) return;

      if (window.innerWidth < 901) {
        host.classList.remove("ready");

        return;
      }

      const box = anchor.getBoundingClientRect();
      const x = box.left + box.width / 2;
      /* `getBoundingClientRect` mide contra la ventana y la costura se coloca
         dentro de `.login`, que empieza en el origen del documento: si la
         pagina esta desplazada hay que sumar ese desplazamiento o la linea
         aparece movida. El diseno original no lo sumaba porque daba por hecho
         que la pagina nunca tiene scroll. */
      const y = box.top + box.height / 2 + window.scrollY;

      host.style.left = `${x}px`;
      host.style.top = `${y - 85}px`;
      host.style.width = `${Math.max(280, window.innerWidth - x + 40)}px`;
      host.classList.add("ready");
    }

    function schedule(): void {
      window.clearTimeout(timer);
      timer = window.setTimeout(place, 120);
    }

    place();
    window.addEventListener("resize", schedule);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", schedule);
    };
  }, [seam, tip]);
}

/**
 * La escena de la izquierda, capa por capa. Es decoracion --la curva, las velas
 * y las etiquetas son un dibujo, no datos de nadie-- y va entera `aria-hidden`.
 *
 * Los identificadores de `<defs>` llevan prefijo `lg-`: son GLOBALES al
 * documento, y `sky`, `grid`, `soft` o `glow` a secas chocarian con cualquier
 * otro SVG de la aplicacion. El del panel, `dots`, `fade` y `soft2`, igual.
 */
function Scene(props: { tipRef: RefObject<SVGCircleElement | null> }): ReactNode {
  return (
    <svg
      className="scene"
      viewBox="0 0 800 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="lg-sky" x1="0" y1="0" x2=".7" y2="1">
          <stop className="sky-1" offset="0" />
          <stop className="sky-2" offset=".55" />
          <stop className="sky-3" offset="1" />
        </linearGradient>
        <pattern id="lg-grid" width="44" height="44" patternUnits="userSpaceOnUse">
          <path className="grid-line" d="M44 0H0V44" fill="none" />
        </pattern>
        <linearGradient id="lg-gline" x1="0" y1="0" x2="1" y2="0">
          <stop className="gline-a" offset="0" />
          <stop className="gline-b" offset="1" />
        </linearGradient>
        <linearGradient id="lg-garea" x1="0" y1="0" x2="0" y2="1">
          <stop className="garea-0" offset="0" />
          <stop className="garea-1" offset="1" />
        </linearGradient>
        <filter id="lg-soft" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="58" />
        </filter>
        <filter id="lg-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect width="800" height="900" fill="url(#lg-sky)" />
      <g>
        <circle
          className="blob b1"
          cx="170"
          cy="200"
          r="210"
          fill="#2F6FED"
          opacity=".30"
          filter="url(#lg-soft)"
        />
        <circle
          className="blob b2"
          cx="690"
          cy="700"
          r="250"
          fill="#12A06B"
          opacity=".24"
          filter="url(#lg-soft)"
        />
        <circle
          className="blob b3"
          cx="640"
          cy="150"
          r="150"
          fill="#E3B94C"
          opacity=".15"
          filter="url(#lg-soft)"
        />
      </g>
      <rect width="800" height="900" fill="url(#lg-grid)" />

      <g fill="#fff">
        {STARS.map((star) => (
          <circle
            key={`${star.cx}-${star.cy}`}
            className="star"
            style={vars({ "--d": star.delay })}
            cx={star.cx}
            cy={star.cy}
            r={star.r}
          />
        ))}
      </g>

      <g>
        <circle
          className="ring"
          cx="648"
          cy="166"
          r="94"
          fill="none"
          strokeDasharray="5 13"
          opacity=".8"
        />
        <circle cx="648" cy="166" r="60" fill="none" stroke="rgba(255,255,255,.09)" />
      </g>

      {/* curva de capital */}
      <g>
        <path
          className="eqarea"
          d="M-20 700 C80 690 140 640 220 652 S330 548 396 566 S500 452 566 470 S700 336 820 362 L820 900 L-20 900 Z"
          fill="url(#lg-garea)"
        />
        <path
          className="eqline"
          d="M-20 700 C80 690 140 640 220 652 S330 548 396 566 S500 452 566 470 S700 336 820 362"
          fill="none"
          stroke="url(#lg-gline)"
          strokeWidth="3.6"
          strokeLinecap="round"
          filter="url(#lg-glow)"
        />
        <circle className="node n1 n-gold" cx="220" cy="652" r="6" />
        <circle className="node n2 n-gold" cx="396" cy="566" r="6" />
        <circle className="node n3 n-gold" cx="566" cy="470" r="6" />
        <circle ref={props.tipRef} className="node n4" cx="760" cy="378" r="7.5" fill="#fff" />
      </g>

      {/* velas */}
      <g opacity=".92">
        {CANDLES.map((candle) => (
          <g key={`${candle.x}-${candle.y}`} transform={`translate(${candle.x} ${candle.y})`}>
            <g className={`candle ${candle.way}`} style={vars({ "--d": candle.delay })}>
              <line x1="0" y1={candle.top} x2="0" y2={candle.bottom} strokeWidth="2" />
              <rect x="-9" y={candle.boxY} width="18" height={candle.boxH} rx="3" />
            </g>
          </g>
        ))}
      </g>

      {/* etiquetas */}
      <g>
        {PILLS.map((pill) => (
          <g key={pill.label} transform={`translate(${pill.x} ${pill.y})`}>
            <g className="art-pill" style={vars({ "--d": pill.delay })}>
              <rect className="pill-box" width={pill.width} height="38" rx="19" />
              <circle cx="21" cy="19" r="5" fill={pill.dot} />
              <text x="36" y="24">
                {pill.label}
              </text>
            </g>
          </g>
        ))}
      </g>
    </svg>
  );
}

/**
 * El fondo vectorial del panel: patron de puntos con su mascara de degradado,
 * dos manchas difusas, los arcos concentricos que giran muy despacio y la cinta
 * de velas que avanza. Decoracion pura, y por eso `aria-hidden`.
 */
function PanelArt(): ReactNode {
  return (
    <svg viewBox="0 0 760 980" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <pattern id="lg-dots" width="26" height="26" patternUnits="userSpaceOnUse">
          <circle className="dot-mark" cx="2" cy="2" r="1.5" />
        </pattern>
        <filter id="lg-soft2" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="72" />
        </filter>
        <linearGradient id="lg-fade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset=".28" stopColor="#fff" stopOpacity="1" />
          <stop offset=".72" stopColor="#fff" stopOpacity="1" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id="lg-dotmask">
          <rect width="760" height="980" fill="url(#lg-fade)" />
        </mask>
      </defs>

      <rect width="760" height="980" fill="url(#lg-dots)" mask="url(#lg-dotmask)" />
      <circle className="wash w1" cx="620" cy="150" r="240" filter="url(#lg-soft2)" />
      <circle className="wash w2" cx="90" cy="820" r="260" filter="url(#lg-soft2)" />

      {/* arcos concentricos */}
      <g className="pring" opacity=".9">
        <circle cx="700" cy="120" r="150" fill="none" strokeDasharray="3 10" />
        <circle cx="700" cy="120" r="112" fill="none" />
        <circle cx="700" cy="120" r="72" fill="none" strokeDasharray="1 8" />
      </g>
      <circle className="pdot" cx="700" cy="8" r="4" />

      {/* La cinta de velas. Veinte velas repetidas dos veces con 600 px de
          separacion: la animacion desplaza exactamente 600 px, asi que el
          segundo grupo entra por la derecha justo cuando el primero sale y el
          bucle no tiene costura. */}
      <g className="tape" opacity=".55">
        <g transform="translate(0 936)">
          {[0, 600].map((offset) =>
            TAPE.map((tick) => (
              <g key={`${offset}-${tick.x}`} transform={`translate(${offset + tick.x})`}>
                <g className={tick.way === "up" ? "t-up" : "t-down"}>
                  <line x1="0" y1={tick.top} x2="0" y2={tick.bottom} strokeWidth="2" />
                  <rect x="-6" y={tick.boxY} width="12" height={tick.boxH} rx="2" />
                </g>
              </g>
            )),
          )}
        </g>
      </g>
      <line className="p-hair" x1="0" y1="884" x2="760" y2="884" />
    </svg>
  );
}

/**
 * La costura. El punto que corre por la curva usa `animateMotion`, que es SMIL
 * y NO lo apaga `prefers-reduced-motion`, asi que se deja de dibujar cuando el
 * sistema pide poco movimiento en vez de confiar en el CSS.
 */
function Seam(): ReactNode {
  const [calm] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  return (
    <svg viewBox="0 0 1000 170" preserveAspectRatio="none">
      <defs>
        <linearGradient id="lg-seam-grad" x1="0" y1="0" x2="1" y2="0">
          <stop className="seam-0" offset="0" />
          <stop className="seam-1" offset=".16" />
          <stop className="seam-2" offset=".45" />
          <stop className="seam-3" offset="1" />
        </linearGradient>
        <path id="lg-seam-path" d="M0 85 C90 78 170 52 280 48 S470 84 600 66 S820 24 1010 40" />
      </defs>
      <use
        href="#lg-seam-path"
        className="sline"
        stroke="url(#lg-seam-grad)"
        vectorEffect="non-scaling-stroke"
      />
      {!calm && (
        <circle className="srun" r="4">
          <animateMotion dur="17s" begin="3.2s" repeatCount="indefinite" calcMode="linear">
            <mpath href="#lg-seam-path" />
          </animateMotion>
        </circle>
      )}
    </svg>
  );
}

/** Las siete estrellas del diseno, con sus radios y sus desfases. */
const STARS = [
  { cx: 96, cy: 560, r: 1.7, delay: "-1s" },
  { cx: 312, cy: 128, r: 1.5, delay: "-2.1s" },
  { cx: 486, cy: 86, r: 1.9, delay: "-.5s" },
  { cx: 734, cy: 452, r: 1.6, delay: "-1.7s" },
  { cx: 188, cy: 760, r: 1.4, delay: "-2.6s" },
  { cx: 560, cy: 742, r: 1.8, delay: "-.9s" },
  { cx: 404, cy: 612, r: 1.4, delay: "-3.1s" },
] as const;

/** Las ocho velas de la escena, con sus mismas medidas y desfases. */
const CANDLES = [
  { x: 126, y: 372, way: "up", delay: "-1s", top: -48, bottom: 48, boxY: -21, boxH: 42 },
  { x: 198, y: 424, way: "down", delay: "-3.3s", top: -31, bottom: 31, boxY: -13, boxH: 27 },
  { x: 270, y: 340, way: "up", delay: "-2s", top: -62, bottom: 54, boxY: -31, boxH: 54 },
  { x: 342, y: 382, way: "down", delay: "-.6s", top: -27, bottom: 37, boxY: -11, boxH: 25 },
  { x: 414, y: 286, way: "up", delay: "-4s", top: -52, bottom: 46, boxY: -27, boxH: 48 },
  { x: 486, y: 328, way: "down", delay: "-1.5s", top: -35, bottom: 41, boxY: -15, boxH: 31 },
  { x: 558, y: 244, way: "up", delay: "-2.7s", top: -60, bottom: 48, boxY: -35, boxH: 60 },
  { x: 716, y: 296, way: "up", delay: "-3.7s", top: -72, bottom: 52, boxY: -41, boxH: 68 },
] as const;

/**
 * Las tres etiquetas flotantes del dibujo, con las medidas del diseno.
 *
 * Una sola palabra cambiada: el diseno ponia "NQ +2.1R", y NQ es un futuro del
 * Nasdaq, un simbolo que este sistema no tiene. V75 si aparece en la
 * especificacion, asi que la escena parece de ESTE producto y no de otro.
 *
 * Las cifras siguen siendo parte del dibujo y no una medida de nadie: el SVG
 * entero va `aria-hidden` y no hay sesion todavia de la que sacar un numero.
 * Lo que no se toca es la regla del ambar: la etiqueta de disciplina lleva el
 * oro, y la de resultado nunca lo lleva.
 */
const PILLS = [
  { x: 96, y: 168, delay: "1.1s", width: 150, dot: "#E3B94C", label: "Acierto 62%" },
  { x: 556, y: 452, delay: "1.5s", width: 138, dot: "#3DD68C", label: "V75 +2.1R" },
  { x: 300, y: 178, delay: "1.9s", width: 176, dot: "#FF6B6B", label: "Plan seguido 77%" },
] as const;

/** Las veinte velas de la cinta del panel, en los 600 px que se repiten. */
const TAPE = [
  { x: 0, way: "up", top: -26, bottom: 26, boxY: -12, boxH: 24 },
  { x: 30, way: "down", top: -18, bottom: 20, boxY: -8, boxH: 16 },
  { x: 60, way: "up", top: -34, bottom: 22, boxY: -18, boxH: 30 },
  { x: 90, way: "up", top: -22, bottom: 28, boxY: -10, boxH: 22 },
  { x: 120, way: "down", top: -16, bottom: 30, boxY: -6, boxH: 18 },
  { x: 150, way: "up", top: -40, bottom: 18, boxY: -22, boxH: 34 },
  { x: 180, way: "down", top: -24, bottom: 24, boxY: -12, boxH: 20 },
  { x: 210, way: "up", top: -30, bottom: 20, boxY: -16, boxH: 28 },
  { x: 240, way: "up", top: -20, bottom: 26, boxY: -9, boxH: 20 },
  { x: 270, way: "down", top: -28, bottom: 22, boxY: -14, boxH: 24 },
  { x: 300, way: "up", top: -36, bottom: 20, boxY: -20, boxH: 32 },
  { x: 330, way: "down", top: -18, bottom: 26, boxY: -8, boxH: 18 },
  { x: 360, way: "up", top: -26, bottom: 26, boxY: -12, boxH: 24 },
  { x: 390, way: "up", top: -22, bottom: 20, boxY: -11, boxH: 22 },
  { x: 420, way: "down", top: -32, bottom: 24, boxY: -17, boxH: 26 },
  { x: 450, way: "up", top: -38, bottom: 18, boxY: -21, boxH: 33 },
  { x: 480, way: "up", top: -24, bottom: 28, boxY: -12, boxH: 23 },
  { x: 510, way: "down", top: -20, bottom: 24, boxY: -9, boxH: 19 },
  { x: 540, way: "up", top: -34, bottom: 20, boxY: -19, boxH: 30 },
  { x: 570, way: "down", top: -26, bottom: 26, boxY: -13, boxH: 22 },
] as const;
